import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useToken } from "../../../server/auth/use-token";
import { useSupabaseLoader } from "../../../server/supabase/hooks";
import { authHeader } from "../../../utils/auth-header";
import { uuidv4 } from "../../../utils/uuid";
import { CeAdminSupabaseAccountService } from "../../generic/server/supabase-account-service";
import { slackAdmin } from "../../slack-admin/slack-admin";

import type {
  AdminAccountLimit,
  AdminSlackApp,
} from "../../generic/server/supabase-account-service";
import type { GridColDef } from "@mui/x-data-grid-pro";
import type { FC } from "react";

const COLUMNS: GridColDef<AdminSlackApp>[] = [
  {
    field: "name",
    headerName: "App Name",
    flex: 1,
    valueGetter: (_value, row) => row.name ?? "—",
  },
  {
    field: "version",
    headerName: "Version",
    width: 100,
  },
  {
    field: "app_id",
    headerName: "Slack App ID",
    flex: 1,
    valueGetter: (_value, row) => row.app_id ?? "Not created",
  },
  {
    field: "verified",
    headerName: "Verified",
    width: 100,
    renderCell: ({ row }) =>
      row.verified ? (
        <Chip label="Yes" color="success" size="small" />
      ) : (
        <Chip label="No" color="default" size="small" />
      ),
  },
  {
    field: "slack_config_id",
    headerName: "Has Config",
    width: 120,
    renderCell: ({ row }) =>
      row.slack_config_id ? (
        <Chip label="Connected" color="success" size="small" />
      ) : (
        <Chip label="No Config" color="warning" size="small" />
      ),
  },
];

export const AccountSlackAppsCard: FC<{ account: AdminAccountLimit }> = ({ account }) => {
  const { data, loading, error, reload } = useSupabaseLoader<AdminSlackApp[], "ce_admin">(
    async ({ supabase }) => {
      const { data: apps, error: err } = await new CeAdminSupabaseAccountService(
        supabase,
      ).getSlackApps(account.id);
      if (err) throw new Error("Could not load Slack apps");
      return apps ?? [];
    },
    [account.id],
    "ce_admin",
  );

  if (loading) return <LinearProgress />;
  if (error) return <Alert severity="error">{error.message}</Alert>;

  return <SlackAppsCardContent account={account} apps={data} onCreated={reload} />;
};

export const SlackAppsCardContent: FC<{
  account: AdminAccountLimit;
  apps: AdminSlackApp[];
  onCreated: () => void;
}> = ({ account, apps, onCreated }) => {
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const getToken = useToken();

  const create = async (): Promise<void> => {
    setConfirmOpen(false);

    if (!account.domain) {
      toast.error("Account does not have a domain set");
      return;
    }

    setCreating(true);
    try {
      const response = await slackAdmin.POST("/create-app", {
        body: {
          slack_app_id: uuidv4(),
          account_id: account.id,
          display_name: name,
          org_name: account.domain,
        },
        ...authHeader(await getToken()),
      });

      if (response.error) {
        const errBody = response.error as { error?: string };
        toast.error(errBody.error ?? "Failed to create Slack app");
        return;
      }

      toast.success(
        `Slack app "${name}" created successfully. Client ID: ${response.data.client_id}`,
      );
      setName("");
      onCreated();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "An unexpected error occurred");
    } finally {
      setCreating(false);
    }
  };

  return (
    <Paper elevation={24} sx={{ p: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Slack Apps
      </Typography>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <TextField
            label="App Name"
            variant="standard"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            helperText="The name for the new Slack app"
          />
          <Button
            data-analytics-id="ce-admin-create-slack-app"
            variant="contained"
            disabled={creating || name.trim() === ""}
            onClick={() => setConfirmOpen(true)}
            sx={{ whiteSpace: "nowrap" }}
          >
            {creating ? "Creating…" : "Create Slack App"}
          </Button>
        </Stack>
        <DataGridPro<AdminSlackApp>
          rows={apps}
          columns={COLUMNS}
          disableRowSelectionOnClick
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
          }}
          pageSizeOptions={[10, 25, 50]}
          pagination
        />
      </Stack>
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Slack App Creation</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will <strong>build and distribute</strong> a new Slack app. This action is
            rate-limited to <strong>1 per day</strong>. Please verify the details below before
            confirming.
          </DialogContentText>
          <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Account:</strong> {account.name}
            </Typography>
            <Typography variant="body2">
              <strong>App Name:</strong> {name}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            data-analytics-id="ce-admin-create-slack-app-cancel"
            onClick={() => setConfirmOpen(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            data-analytics-id="ce-admin-create-slack-app-confirm"
            onClick={() => {
              void create();
            }}
            variant="contained"
            color="warning"
            disabled={creating}
          >
            Confirm &amp; Create
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
